import { memo, useCallback } from 'react';
import { Channel } from '@domain/eventTeam';
import classNames from 'classnames';

import { RowElement } from './row-element';
import { ValidationError } from './validators';

const ERROR_STYLE_BY_POSITION = {
  nested: { mobile: 'ml-14', desktop: 'ml-20' },
  standalone: { mobile: 'ml-4', desktop: 'ml-0' },
} as const;

interface ChannelRowProps {
  channel: Channel;
  isMobileViewPort: boolean;
  error?: ValidationError;
  isNested: boolean;
  updateChannelMax: (channelId: string, max: number | null) => void;
  setChannelSalesClosed: (channelId: string, isClosed: boolean) => void;
  canCloseSales: boolean;
  labelClassName?: string;
}

// Memoised so that editing one channel does not re-render every other channel
// in the list. Receives the handlers as props (rather than consuming the team
// channel context internally) so the memo isn't bypassed by context value
// updates when an unrelated channel's error changes.
export const ChannelRow = memo<ChannelRowProps>(
  ({
    channel,
    isMobileViewPort,
    error,
    isNested,
    updateChannelMax,
    setChannelSalesClosed,
    canCloseSales,
    labelClassName,
  }) => {
    const channelId = channel.channel.id;

    const handleChange = useCallback(
      (next: number | null) => updateChannelMax(channelId, next),
      [channelId, updateChannelMax]
    );

    const handleToggle = useCallback(
      (isClosed: boolean) => setChannelSalesClosed(channelId, isClosed),
      [channelId, setChannelSalesClosed]
    );

    const position = isNested ? 'nested' : 'standalone';
    const viewport = isMobileViewPort ? 'mobile' : 'desktop';
    const errorStyles = ERROR_STYLE_BY_POSITION[position][viewport];

    return (
      <RowElement
        rowId={`channel-${channelId}`}
        name={channel.channel.name}
        isMobileViewPort={isMobileViewPort}
        isSalesClosed={channel.isTicketSalesClosed}
        maxTicketQuantity={channel.maxTicketQuantity}
        soldItems={channel.soldItems.tickets}
        errorStyles={errorStyles}
        error={error}
        onMaxQuantityChange={handleChange}
        onToggleSalesClosed={handleToggle}
        canCloseSales={canCloseSales}
        labelClassName={labelClassName}
        className={isNested ? classNames({ 'ml-20': !isMobileViewPort, 'ml-10': isMobileViewPort }) : undefined}
      />
    );
  }
);

ChannelRow.displayName = 'ChannelRow';
